import { setConfig } from "@/store/slices/appSlice";
import { store } from "@/store/store";
import constants from "@/config/constants.json"
import { sanitizeAppConfigResponse, setStoredAppConfig } from "@/utils/appConfigStorage";

export interface AboutPageConfig {
    published: number;
    banner_image: any | null;
    banner_intro_text: string | null;
    banner_title: string | null;
    section_title_left: string | null;
    section_text_1:string | null;
    section_text_2:string | null;
    section_text_3:string | null;
    section_text_4:string | null;
    section_title_right: string | null;
    section_content: string | null;
    card_section_title: string | null;
    cta_image: any | null;
    cta_title: string | null;
    cta_description: string | null;
    banner_subtitle: string | null;
    created_at: string;
    updated_at: string;
    seo_image: any;
    seo_title: string | null;
    seo_description: string | null;
    seo_keywords: string | null;
    seo_robots: string | null;
    seo_page_title: string | null;
    seo_og_type: string | null;
}

export interface AboutCardConfig {
    id: number;
    published: number;
    icon: string | null;
    title: string | null;
    text: string | null;
    pos: number;
    created_at: string;
    updated_at: string;
}

export interface AboutConfig {
    page: AboutPageConfig | null;
    cards: AboutCardConfig[];
}

export interface ContactPageConfig {
    banner_intro_text: string | null;
    banner_image: any | null;
    banner_title: string | null;
    banner_description: string | null;
    section_intro_text: string | null;
    section_title: string | null;
    section_text: string | null;
    form_btn_text: string | null;
    follow_text: string | null;
    pos: number;
    created_at: string | null;
    updated_at: string | null;
    seo_image: any;
    seo_title: string | null;
    seo_description: string | null;
    seo_keywords: string | null;
    seo_robots: string | null;
    seo_page_title: string | null;
    seo_og_type: string | null;
}

export interface ContactConfig {
    page: ContactPageConfig | null;
}

export interface SiteSettingsConfig {
    id: number;
    email: string | null;
    send_form_messages_to: string | null;
    address_text: string | null;
    instagram_url: string | null;
    phone_number: string | null;
    facebook_url: string | null;
    linkedin_url: string | null;
    twitter_url: string | null;
    copyright_text: string | null;
    pos: number;
    created_at: string | null;
    updated_at: string | null;
    seo_page_title: string | null;
    seo_og_type: string | null;
}

export interface HomePageConfig {
    id?: number;
    banner_title: string | null;
    banner_image: any | null;
    banner_description: string | null;
    btn_left_text: string | null;
    btn_right_text: string | null;
    solutions_title: string | null;
    cta_loop_text: string | null;
    services_title?:string | null;
    services_subtitle?:string | null;
    process_title: string | null;
    process_subtitle: string | null;
    cta_title: string | null;
    cta_description: string | null;
    cta_image: any | null;
    why_darperso_title: string | null;
    why_darperso_items: string | null;
    pos: number;
    created_at: string | null;
    updated_at: string | null;
    seo_image: any;
    seo_title: string | null;
    seo_description: string | null;
    seo_keywords: string | null;
    seo_robots: string | null;
    seo_page_title: string | null;
    seo_og_type: string | null;
}

export interface HomeProcessConfig {
    published: number;
    title: string | null;
    content: string | null;
    pos: number;
    created_at: string | null;
    updated_at: string | null;
}

export interface HomeConfig {
    page: HomePageConfig | null;
    process: HomeProcessConfig[];
}

export interface ServiceConfig {
    id: number;
    published: number;
    banner_intro_text: string | null;
    banner_image: any | null;
    banner_title: string | null;
    banner_description: string | null;
    key: string | null;
    slug: string | null;
    looping_text: string | null;
    cta_title: string | null;
    cta_description: string | null;
    cta_button_text: string | null;
    cta_image: any | null;
    card_icon: string | null;
    card_title: string | null;
    card_description: string | null;
    section_title: string | null;
    section_lottie: string | null;
    section_content: string | null;
    service_order: number;
    pos: number;
    created_at: string | null;
    updated_at: string | null;
    seo_image: any;
    seo_title: string | null;
    seo_description: string | null;
    seo_keywords: string | null;
    seo_robots: string | null;
    seo_page_title: string | null;
    seo_og_type: string | null;
    faq_title:string | null;
    faq_subtitle:string | null;
    faqItems?:Array<{
        title:string;
        subtitle:string;
        service_id:any;
    }>
}

export interface SeoSettingsConfig {
    id: number;
    test: string | null;
    created_at: string | null;
    updated_at: string | null;
    pos: number;
    seo_image: any;
    seo_title: string | null;
    seo_description: string | null;
    seo_keywords: string | null;
    seo_robots: string | null;
    seo_site_name: string | null;
    seo_og_type: string | null;
    seo_page_title: string | null;
}

export interface AppConfigResponse {
    about?: AboutConfig;
    contact?: ContactConfig;
    home?: HomeConfig;
    services?: ServiceConfig[];
    site_settings?: SiteSettingsConfig;
    seo_settings?: SeoSettingsConfig;
}

export const fetchAppConfigData = async () => {
    const response = await fetch(constants.api);

    if (!response.ok) {
        throw new Error(`Failed to fetch app config: ${response.status}`);
    }
    const data = sanitizeAppConfigResponse(await response.json())

    if (Object.keys(data).length > 0) {
        setStoredAppConfig(data)
        store.dispatch(setConfig(data))
    }

    return data;
};
